import { mergeAttributes, type Range } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { Image } from '../image'
import { ImageBlockView } from './components/ImageBlockView'

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		imageBlock: {
			setImageBlock: (attributes: { src: string }) => ReturnType
			setImageBlockAt: (attributes: {
				src: string
				pos: number | Range
			}) => ReturnType
			setImageBlockLink: (link: string) => ReturnType
			setImageBlockAlign: (align: 'left' | 'center' | 'right') => ReturnType
			setImageBlockWidth: (width: number) => ReturnType
			setImageBlockRadius: (radius: number) => ReturnType
		}
	}
}

export const ImageBlock = Image.extend({
	name: 'imageBlock',

	group: 'block',

	defining: true,

	isolating: true,

	addAttributes() {
		return {
			link: {
				default: '',
			},
			src: {
				default: '',
				parseHTML: element => element.getAttribute('src'),
				renderHTML: attributes => ({
					src: attributes.src,
				}),
			},
			width: {
				default: 100,
				parseHTML: element => element.getAttribute('data-width'),
				renderHTML: attributes => ({
					'data-width': attributes.width,
					width: `${attributes.width}%`,
				}),
			},
			radius: {
				default: 4,
				parseHTML: element => element.getAttribute('data-radius'),
				renderHTML: attributes => ({
					'data-radius': attributes.radius,
					radius: `${attributes.radius}px`,
				}),
			},
			height: {
				default: 'auto',
				parseHTML: element => element.getAttribute('data-height'),
				renderHTML: attributes => ({
					'data-height': attributes.height,
					height: attributes.height,
				}),
			},
			align: {
				default: 'center',
				parseHTML: element => element.getAttribute('data-align'),
				renderHTML: attributes => ({
					'data-align': attributes.align,
				}),
			},
			alt: {
				default: 'image',
				parseHTML: element => element.getAttribute('alt'),
				renderHTML: attributes => ({
					alt: attributes.alt,
				}),
			},
			style: {
				parseHTML: element => element.getAttribute('style'),
				renderHTML: attributes => ({
					style: `width: ${attributes.width}%;border-radius: ${attributes.radius}px;${attributes.align === 'right' ? 'margin-right: 0px;' : ''}${attributes.align === 'left' ? 'margin-left: 0px;' : ''}${attributes.align === 'center' ? 'margin-left: auto; margin-right: auto;' : ''}`,
				}),
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'img:not([src^="data:"])',
			},
		]
	},

	renderHTML({ HTMLAttributes }) {
		return HTMLAttributes.link
			? [
					'a',
					{
						href: HTMLAttributes.link,
						target: '_blank',
						rel: 'noopener noreferrer',
					},
					['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)],
				]
			: ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
	},

	addCommands() {
		return {
			setImageBlock:
				attrs =>
				({ commands }) => {
					return commands.insertContent({
						type: 'imageBlock',
						attrs: { src: attrs.src },
					})
				},

			setImageBlockAt:
				attrs =>
				({ commands }) => {
					return commands.insertContentAt(attrs.pos, {
						type: 'imageBlock',
						attrs: { src: attrs.src },
					})
				},

			setImageBlockAlign:
				align =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', { align }),

			setImageBlockWidth:
				width =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', {
						width: `${Math.max(0, Math.min(100, width))}%`,
					}),

			setImageBlockRadius:
				radius =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', {
						radius: `${String(radius)}`,
					}),

			setImageBlockLink:
				link =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', {
						link,
					}),
		}
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageBlockView)
	},
})

export default ImageBlock
